/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Projects landing page showcasing open source work, experiments, and personal projects.
 */

import { createFileRoute, Outlet, useMatches, Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { createPageMeta, createCollectionJsonLd, PAGE_KEYWORDS } from '@/lib/meta';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  Button,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { pageColors, getGradient } from '@/theme/colors';
import { ProjectCard } from '@/components/ProjectCard';
import { ProjectModal } from '@/components/ProjectModal';
import { Project, ProjectCategory, ProjectStatus } from '@/types/project';
import { analytics } from '@/lib/analytics';

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
  pendingMs: 150,
  loader: async ({ context }) => {
    context.queryClient.prefetchQuery(trpc.projects.tags.queryOptions(undefined, { staleTime: 60_000 }));

    await context.queryClient.ensureQueryData(trpc.projects.list.queryOptions({
      q: undefined,
      category: 'all',
      status: 'all',
      tags: [],
    }));
  },
  head: ({ matches }) => {
    if (matches.some(match => match.id.includes('/projects/repos'))) return {};
    const base = createPageMeta({
      title: 'Projects - Christian Battaglia',
      description: 'Open source projects, tools, and systems built by Christian Battaglia. From NGINX/Lua libraries to music analysis tools and multi-framework integrations.',
      path: '/projects',
      image: '/og/projects.png',
      imageAlt: 'Projects — Christian Battaglia',
      keywords: PAGE_KEYWORDS.projects,
    });
    return {
      ...base,
      scripts: [
        createCollectionJsonLd({
          name: 'Projects — Christian Battaglia',
          description: 'Open source projects, tools, and systems built by Christian Battaglia.',
          path: '/projects',
          keywords: PAGE_KEYWORDS.projects,
        }),
      ],
    };
  },
});

function ProjectsPage() {
  const matches = useMatches();
  const isChildRoute = matches.some(match =>
    match.routeId !== '/projects' && match.routeId?.startsWith('/projects/'),
  );
  if (isChildRoute) {
    return <Outlet />;
  }

  return <ProjectsIndex />;
}

function ProjectsIndex() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const cyan = dark ? pageColors.projects.accent : pageColors.projects.primary;
  const cyanBg = dark ? 'rgba(103, 232, 249, 0.12)' : 'rgba(6, 182, 212, 0.1)';
  const cyanBgHover = dark ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.2)';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ProjectCategory | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = useState<ProjectStatus | 'all'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const queryInput = useMemo(() => ({
    q: searchQuery || undefined,
    category: selectedCategory,
    status: selectedStatus,
    tags: selectedTags,
  }), [searchQuery, selectedCategory, selectedStatus, selectedTags]);

  const { data: listData } = useQuery({
    ...trpc.projects.list.queryOptions(queryInput),
    placeholderData: keepPreviousData,
  });
  const { data: tagsData } = useQuery(trpc.projects.tags.queryOptions(undefined, { staleTime: 60_000 }));

  const allProjects = listData?.projects ?? [];
  const filteredProjects = allProjects;
  const allTags = useMemo(() => (tagsData ?? []).map(t => t.tag), [tagsData]);

  const totalCount = listData?.totalUnfiltered ?? allProjects.length;
  const featuredCount = listData?.featuredCount ?? 0;
  const activeProjectsCount = listData?.activeCount ?? 0;

  const projSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!searchQuery) return;
    if (projSearchTimerRef.current) clearTimeout(projSearchTimerRef.current);
    projSearchTimerRef.current = setTimeout(() => {
      analytics.searchExecuted(searchQuery, 'projects', filteredProjects.length);
    }, 600);
    return () => { if (projSearchTimerRef.current) clearTimeout(projSearchTimerRef.current); };
  }, [searchQuery, filteredProjects.length]);

  const handleLearnMore = (project: Project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedProject(null), 200);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
    analytics.filterApplied('projects', 'tag', tag);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedStatus('all');
    setSelectedTags([]);
    analytics.filtersCleared('projects');
  };

  const hasActiveFilters =
    searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all' || selectedTags.length > 0;

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: getGradient(pageColors.projects, dark ? 'dark' : 'light'),
          color: 'white',
          py: { xs: 6, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left: Title + Stats */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  Projects
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    opacity: 0.95,
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
                  @cdbattags
                </Typography>

                {/* Stats Card */}
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Overview
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {totalCount} projects
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Total portfolio
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {activeProjectsCount} active
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Currently maintained
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                          {featuredCount} featured
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Highlighted work
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right: Description */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600 }}>
                  Building Tools & Systems
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  From high-performance NGINX libraries to music analysis platforms, these projects represent
                  explorations in system architecture, performance optimization, and creative technical solutions.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  Each project tackles a specific problem space—whether it's making JWT validation lightning-fast
                  in NGINX, integrating multiple JavaScript frameworks, or analyzing music listening patterns over time.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  href="https://github.com/cdbattags"
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  sx={{
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  View All on GitHub
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sub-Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={0}>
            <Button
              component={Link}
              to="/projects"
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 0,
                borderBottom: '2px solid',
                borderColor: cyan,
                color: cyan,
                fontWeight: 600,
                '&:hover': { bgcolor: cyanBg },
              }}
            >
              Featured Projects
            </Button>
            <Button
              component={Link}
              to="/projects/repos"
              sx={{
                py: 1.5,
                px: 3,
                borderRadius: 0,
                borderBottom: '2px solid transparent',
                color: 'text.secondary',
                fontWeight: 500,
                '&:hover': { bgcolor: cyanBg, color: cyan },
              }}
            >
              GitHub Repos
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Search and Filters */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Stack spacing={3}>
            {/* Search */}
            <TextField
              fullWidth
              placeholder="Search projects by name, description, or technology..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Filters Row */}
            <Grid container spacing={2}>
              {/* Category Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Category"
                    onChange={e => { const v = e.target.value as ProjectCategory | 'all'; setSelectedCategory(v); analytics.filterApplied('projects', 'category', v); }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    <MenuItem value="open-source">Open Source</MenuItem>
                    <MenuItem value="personal">Personal</MenuItem>
                    <MenuItem value="experiment">Experiment</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    label="Status"
                    onChange={e => { const v = e.target.value as ProjectStatus | 'all'; setSelectedStatus(v); analytics.filterApplied('projects', 'status', v); }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                    <MenuItem value="coming-soon">Coming Soon</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                    fullWidth
                    sx={{ height: '40px' }}
                  >
                    Clear All Filters
                  </Button>
                </Grid>
              )}
            </Grid>

            {/* Tag Filter Chips */}
            {allTags.length > 0 && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <FilterIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Filter by Technology ({selectedTags.length} selected)
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {allTags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onClick={() => handleTagToggle(tag)}
                      sx={{
                        bgcolor: selectedTags.includes(tag) ? cyan : cyanBg,
                        color: selectedTags.includes(tag) ? (dark ? '#0a0a0a' : 'white') : cyan,
                        fontWeight: selectedTags.includes(tag) ? 600 : 500,
                        '&:hover': {
                          bgcolor: selectedTags.includes(tag) ? (dark ? '#22d3ee' : '#0891b2') : cyanBgHover,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Results Count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredProjects.length} of {totalCount} projects
            {hasActiveFilters && (
              <Typography component="span" sx={{ ml: 1, fontWeight: 600, color: cyan }}>
                (filtered)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <Grid container spacing={3}>
            {filteredProjects.map(project => (
              <Grid key={project.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <ProjectCard project={project} onLearnMore={handleLearnMore} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try adjusting your search or filters
            </Typography>
            <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
              Clear Filters
            </Button>
          </Paper>
        )}

        {/* GitHub CTA */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              background: dark
                ? 'linear-gradient(135deg, rgba(103, 232, 249, 0.06) 0%, rgba(34, 211, 238, 0.06) 100%)'
                : 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(34, 211, 238, 0.05) 100%)',
              border: '1px solid',
              borderColor: dark ? 'rgba(103, 232, 249, 0.2)' : 'rgba(6, 182, 212, 0.2)',
            }}
          >
            <Stack spacing={2} alignItems="center">
              <GitHubIcon sx={{ fontSize: 48, color: cyan }} />
              <Typography variant="h5" fontWeight="bold">
                Explore More on GitHub
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                These are just highlights. Check out my GitHub for more experiments, contributions to open source,
                and code explorations across different languages and frameworks.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<GitHubIcon />}
                href="https://github.com/cdbattags"
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                sx={{
                  background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                  },
                }}
              >
                Visit GitHub Profile
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Container>

      {/* Project Detail Modal */}
      <ProjectModal project={selectedProject} open={modalOpen} onClose={handleCloseModal} />
    </>
  );
}
